import { acceptHMRUpdate, defineStore } from 'pinia'
import i18n from '@/i18n'
import { useDrawingStore } from '@/stores/drawing'

export const useFieldSourceInfoStore = defineStore('fieldSourceInfo', {
    state: () => ({
        fieldSourceInfo: '',
        isOldSourceInfo: false,
        isManualTextSaved: false,
        incomingSourceInfo: null
    }),
    actions: {
        setFieldSourceInfo(sourceInfo) {
            if (typeof sourceInfo === 'object') {
                const source = this.validateSourceInfo(sourceInfo)
                if (source) {
                    this.fieldSourceInfo = source
                    this.incomingSourceInfo = sourceInfo
                } else {
                    this.fieldSourceInfo = ''
                }
            } else {
                if (sourceInfo.trim() !== '') {
                    this.fieldSourceInfo = sourceInfo
                    this.isOldSourceInfo = true
                } else {
                    this.fieldSourceInfo = ''
                }
            }
        },
        setManualText(text) {
            if (text) {
                this.fieldSourceInfo = text
                this.isManualTextSaved = true
            }
        },
        resetFieldSourceInfo() {
            this.fieldSourceInfo = ''
            this.isManualTextSaved = false
            this.isOldSourceInfo = false
            this.incomingSourceInfo = null
        },
        validateSourceInfo(source) {
            if (!source || Object.keys(source).length === 0) {
                return false
            }

            // TRIMBLE
            // source.vendor is set in the shapefileExport.js script when feature is downloaded to shapefile from Trimble API
            // source.correction, source.device, source.correcti01 are used when feature is downloaded from Trimble website
            const isTrimble = (source.vendor === 'Trimble') || (source.correction && source.device && source.correcti01)
            if (isTrimble) {
                return 'Trimble'
            }

            // JOHN DEERE
            const isJohnDeere = (
                source.client_nam && source.farm_name && source.field_name &&
                source.client_id && source.farm_id && source.field_id &&
                source.org_id &&
                typeof source.polygontyp === 'number' && source.polygontyp >= 0
            )
            if (isJohnDeere) {
                return 'John Deere'
            }

            return false
        },
        getSourceInfoType() {
            // Device sources (Trimble, John Deere)
            const validSourceInfo = this.validateSourceInfo(this.incomingSourceInfo)
            if (validSourceInfo) {
                return {
                    hasLabel: true,
                    value: validSourceInfo
                }
            }

            // Manual drawing
            const isManualDrawing = this.fieldSourceInfo === 'manual_drawn'
            if (isManualDrawing) {
                return {
                    hasLabel: true,
                    value: 'Manual drawn'
                }
            }

            // Manual text saved successfully (only when saved, not during editing)
            if (typeof this.fieldSourceInfo === 'string' && this.fieldSourceInfo.trim() && this.isManualTextSaved) {
                return {
                    hasLabel: true,
                    value: this.fieldSourceInfo
                }
            }

            // Old source info
            if (this.isOldSourceInfo) {
                return {
                    hasLabel: true,
                    value: this.fieldSourceInfo
                }
            }

            // No source
            return {
                hasLabel: false,
                value: i18n.global.t('Please add a field source info:')
            }
        },
        isFieldSourceInfoRequired() {
            const drawingStore = useDrawingStore()
            return !this.getSourceInfoType().hasLabel && Boolean(drawingStore.surveyOuterBorderFile)
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFieldSourceInfoStore, import.meta.hot))
}